import { Button, Col, Form, Row, Select, Space, theme } from "antd";
import { useEffect, useState } from "react";
import { SearchQuery } from "../../code/search-query/search-query";
import {
  metricsViewColumns,
  mapMetricsViewQuery,
} from "./OrganizationalMetricsView.default";
import { DataRequest } from "../../controls/Data.default";
import {
  getOrganizationalMetrics,
  getCompanyDepartments,
  getCompanyFiscalPeriods,
} from "../../repository/data.repository";
import { MAX_PAGE_SIZE, MetricType } from "../../repository/data.default";
import Data from "../../controls/Data";

const METRIC_TYPES: MetricType[] = [
  "Absenteeism",
  "Turnover",
  "Grievance",
  "Accident",
];

interface Option {
  readonly label: string;
  readonly value: string;
}

export interface OrganizationalMetricsViewProps {
  readonly trigger?: string;
  readonly onAction?: (key: string, record: any) => void;
}

function OrganizationalMetricsView(props: OrganizationalMetricsViewProps) {
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const values = Form.useWatch([], form);

  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState<Option[]>([]);
  const [periods, setPeriods] = useState<Option[]>([]);
  const [context, setContext] = useState<{ searchQuery: SearchQuery }>({
    searchQuery: { conditions: [] },
  });

  // Departments + fiscal periods are auto-scoped to the manager's company server-side.
  useEffect(() => {
    const scoped = {
      searchQuery: { conditions: [] },
      pageNumber: 0,
      pageSize: MAX_PAGE_SIZE,
    };
    getCompanyDepartments(scoped)
      .then(({ data }) =>
        setDepartments(data.map((d) => ({ label: d.name, value: d.uid }))),
      )
      .catch(() => setDepartments([]));
    getCompanyFiscalPeriods(scoped)
      .then(({ data }) =>
        setPeriods(data.map((p) => ({ label: p.name, value: p.uid }))),
      )
      .catch(() => setPeriods([]));
  }, []);

  useEffect(() => {
    setContext({ ...context });
  }, [props.trigger]);

  function onLoading(loading: boolean): void {
    setLoading(loading);
  }

  function onSearchData(): void {
    setContext({ searchQuery: mapMetricsViewQuery(values) });
  }

  function onClearData(): void {
    form.resetFields();
    setContext({ searchQuery: { conditions: [] } });
  }

  function onAction(key: string, record: any): void {
    props.onAction?.(key, record);
  }

  const request = ({ context, pageNumber, pageSize, sort }: DataRequest) => {
    const sortBy = (sort || [])[0]?.key ?? null;
    const sortDescending = (sort || [])[0]?.direction === "desc";

    return getOrganizationalMetrics({
      ...context,
      pageNumber: 0,
      pageSize: 100,
      ...(sortBy ? { sortBy, sortDescending } : {}),
    }).then(({ data, totalRecords }) => ({
      data,
      pageNumber,
      pageSize,
      total: totalRecords,
    }));
  };

  return (
    <Row className="flex flex-auto flex-row flex-nowrap overflow-hidden">
      <Col
        className="mr-4 flex flex-col border border-solid pl-2 pr-2"
        style={{
          borderColor: token.colorBorder,
          borderRadius: token.borderRadius,
        }}
      >
        <Space className="flex flex-row justify-end pb-2 pt-2">
          <Button disabled={loading} onClick={onClearData}>
            Clear
          </Button>
          <Button type="primary" loading={loading} onClick={onSearchData}>
            Search
          </Button>
        </Space>
        <Form
          className="my-form-item flex max-w-96 flex-col"
          form={form}
          layout="vertical"
          autoComplete="off"
          variant="filled"
          colon={false}
        >
          <Form.Item className="m-0.5 w-80" name="metricType" label="Metric">
            <Select
              allowClear
              placeholder="Any"
              options={METRIC_TYPES.map((t) => ({ label: t, value: t }))}
            />
          </Form.Item>
          <Form.Item className="m-0.5 w-80" name="departmentUid" label="Department">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Any"
              options={departments}
            />
          </Form.Item>
          <Form.Item
            className="m-0.5 w-80"
            name="companyFiscalPeriodUid"
            label="Fiscal period"
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Any"
              options={periods}
            />
          </Form.Item>
        </Form>
      </Col>
      <Data
        columns={metricsViewColumns}
        context={context}
        pageSize={100}
        actions={[{ key: "update", label: "Edit", handler: onAction }]}
        request={request}
        rowKey={({ uid }) => uid}
        onLoading={onLoading}
      />
    </Row>
  );
}

export default OrganizationalMetricsView;
